/**
 * MapLibre custom 3D layer that renders a GLB aircraft model on top of each
 * height-hologram pillar. The models (see `aircraftModels.ts`) are exported at
 * a 1000 m wingspan so they match the fill-extrusion silhouette footprint
 * (`PILLAR_HALF_SPAN_M = 500`).
 *
 * The layer reads the same filtered aircraft list + extrapolation delta used
 * by the GeoJSON RAF loop in `FlightMap.tsx`, so the 3D model moves in lock-
 * step with the ground icon and the colored pillar.
 */

import maplibregl from 'maplibre-gl';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import type { RichAircraft } from '../data/enrich';
import { extrapolate, extrapolationDtFor } from '../lib/geo';
import { isEmergency, usesIconMatchedFootprint } from './aircraftIcons';
import { aircraftDisplayLabel, FLIGHT_PATH_LOOKAHEAD_SEC } from './aircraftSource';
import { pillarHalfSpanM } from './aircraftSource';
import {
  aircraftSceneAltitudeM,
  altitudeHeightAtZoom,
  inferFlightPathPitch,
  lngLatToEnu,
} from './aircraftAltitude';
import { allModelUrls, getAircraftModelDef } from './aircraftModels';

export const AIRCRAFT_MODELS_LAYER_ID = 'aircraft-altitude-models';

const LABEL_MIN_ZOOM = 7;
/** Match ground `aircraft-label` text-size stops (zoom 7 → 9px, zoom 12+ → 12px). */
const LABEL_SCREEN_PX_MIN = 9;
const LABEL_SCREEN_PX_MAX = 12;
/** Starboard/up offset in screen pixels from the aircraft anchor to the label centre. */
const LABEL_SCREEN_OFFSET_X = 46;
const LABEL_SCREEN_OFFSET_Y = -22;
const LABEL_CONNECTOR_COLOR = 0x94a3b8;
const LABEL_CONNECTOR_OPACITY = 0.55;
const PICK_MIN_RADIUS_PX = 18;
const MIN_PATH_SPEED_KT = 20;
const NOSE_LOCAL_Y = 0.48;
const NOSE_LOCAL_Z = 0.12;

/**
 * Yaw correction (degrees) added to `track` when orienting a model. The glTF
 * export places the nose along ±Z (not +Y); after the Rx(+90°) orientation
 * fix the effective heading is 90° clockwise from `track`, so we subtract 90°
 * here to match the ground icon and hologram silhouette.
 */
export const MODEL_YAW_OFFSET_DEG = -90;

/**
 * Stylized rotor span for helicopter GLBs at full zoom. Grows modestly when
 * zoomed out (capped at 6×) so models stay visible without dwarfing airliners.
 */
const HELICOPTER_MODEL_WINGSPAN_BASE_M = 240;
const HELICOPTER_MODEL_ZOOM_MAX_FACTOR = 6;
/** Zoom where the base wingspan looks correct on screen. */
const HELICOPTER_MODEL_ZOOM_REF = 12;
/** Map zoom treated as "very far" — footprint stops at 6× base. */
const HELICOPTER_MODEL_ZOOM_FAR = 2;

function helicopterModelFootprintM(zoom: number): number {
  const base = HELICOPTER_MODEL_WINGSPAN_BASE_M;
  const max = base * HELICOPTER_MODEL_ZOOM_MAX_FACTOR;
  if (zoom >= HELICOPTER_MODEL_ZOOM_REF) return base;
  if (zoom <= HELICOPTER_MODEL_ZOOM_FAR) return max;
  const t = (HELICOPTER_MODEL_ZOOM_REF - zoom) / (HELICOPTER_MODEL_ZOOM_REF - HELICOPTER_MODEL_ZOOM_FAR);
  return base + t * (max - base);
}

interface RenderState {
  list: RichAircraft[];
  snapshotTimeSec: number;
  nowSec: number;
  selectedIcao: string | null;
  selectedFetchedAtSec: number;
  preferLabelCallsign: boolean;
  show3DLabels: boolean;
  show3DPaths: boolean;
}

interface LoadedModel {
  url: string;
  scene: THREE.Group;
  /** Wingspan in model-local units (used to normalize to MODEL_WINGSPAN_M). */
  span: number;
}

type CustomLayerInterface = maplibregl.CustomLayerInterface;

/** MapLibre custom-layer render callback extras (see CustomRenderMethodInput). */
interface CustomRenderOptions {
  nearZ: number;
  farZ: number;
  projectionMatrix: number[];
}

/**
 * Widen the clip planes so airborne models are not clipped when the camera is
 * pitched and zoomed in close (MapLibre default near/far is tight for ground).
 */
function projectionMatrixWithExtendedClip(
  mercatorMatrix: number[],
  options: CustomRenderOptions,
  anchorMerc: maplibregl.MercatorCoordinate,
  scalePerMeter: number,
): THREE.Matrix4 {
  const near = 0.1;
  const far = 1e9;

  const pv = new THREE.Matrix4().fromArray(mercatorMatrix);
  const p = new THREE.Matrix4().fromArray(options.projectionMatrix);
  const invP = p.clone().invert();

  const pWide = p.clone();
  const pe = pWide.elements;
  pe[10] = (far + near) / (near - far);
  pe[14] = (2 * far * near) / (near - far);

  const v = invP.multiply(pv);
  const pvWide = pWide.multiply(v);

  const local = new THREE.Matrix4()
    .makeTranslation(anchorMerc.x, anchorMerc.y, anchorMerc.z)
    .scale(new THREE.Vector3(scalePerMeter, -scalePerMeter, scalePerMeter));

  return pvWide.multiply(local);
}

interface LayerOptions {
  getState: () => RenderState;
  /** When true, the layer skips drawing (top-down view or 3D models off). */
  isHidden: () => boolean;
}

interface LayerState {
  map?: maplibregl.Map;
  scene: THREE.Scene;
  camera: THREE.Camera;
  renderer?: THREE.WebGLRenderer;
  models: Map<string, LoadedModel>;
  loader: GLTFLoader;
  instances: THREE.Object3D[];
  instanceIndex: Map<string, number>;
  pathLines: THREE.Line[];
  pathIndex: Map<string, number>;
  labelMeshes: THREE.Mesh[];
  labelIndex: Map<string, number>;
  labelConnectors: THREE.Line[];
  labelConnectorIndex: Map<string, number>;
  projectionMatrix?: THREE.Matrix4;
  noseLocal: THREE.Vector3;
  labelScratch: THREE.Vector3;
  unprojectScratch: THREE.Vector4;
}

export interface AircraftModelLayer {
  layer: CustomLayerInterface;
  pickAtPoint: (map: maplibregl.Map, point: maplibregl.PointLike) => string | null;
  hasHoverAtPoint: (map: maplibregl.Map, point: maplibregl.PointLike) => boolean;
}

/**
 * Create the MapLibre custom layer. Add it after `aircraft-altitude-pillar`
 * inside `map.on('load')` so models draw on top of the colored pillars.
 */
export function createAircraftModelLayer(opts: LayerOptions): AircraftModelLayer {
  const state: LayerState = {
    scene: new THREE.Scene(),
    camera: new THREE.Camera(),
    models: new Map(),
    loader: new GLTFLoader(),
    instances: [],
    instanceIndex: new Map(),
    pathLines: [],
    pathIndex: new Map(),
    labelMeshes: [],
    labelIndex: new Map(),
    labelConnectors: [],
    labelConnectorIndex: new Map(),
    noseLocal: new THREE.Vector3(),
    labelScratch: new THREE.Vector3(),
    unprojectScratch: new THREE.Vector4(),
  };

  const layer: CustomLayerInterface = {
    id: AIRCRAFT_MODELS_LAYER_ID,
    type: 'custom',
    renderingMode: '3d',
    onAdd(map: maplibregl.Map, gl: WebGLRenderingContext | WebGL2RenderingContext) {
      state.map = map;
      state.renderer = new THREE.WebGLRenderer({
        canvas: map.getCanvas(),
        context: gl,
        antialias: true,
      });
      state.renderer.autoClear = false;

      const ambient = new THREE.AmbientLight(0xffffff, 0.6);
      const key = new THREE.DirectionalLight(0xffffff, 1.0);
      key.position.set(0, -70, 100).normalize();
      const fill = new THREE.DirectionalLight(0xffffff, 0.55);
      fill.position.set(0, 70, 100).normalize();
      state.scene.add(ambient, key, fill);

      for (const url of allModelUrls()) void loadModel(url, state);
    },

    render(_gl, matrix, options) {
      const map = state.map;
      const renderer = state.renderer;
      if (!map || !renderer) return;
      if (opts.isHidden()) {
        renderer.resetState();
        state.projectionMatrix = undefined;
        for (const line of state.pathLines) line.visible = false;
        for (const mesh of state.labelMeshes) mesh.visible = false;
        for (const line of state.labelConnectors) line.visible = false;
        return;
      }

      const renderState = opts.getState();
      const zoom = map.getZoom();

      const center = map.getCenter();
      const anchor = { lon: center.lng, lat: center.lat };
      const anchorElevM = map.queryTerrainElevation([anchor.lon, anchor.lat]) ?? 0;
      const anchorMerc = maplibregl.MercatorCoordinate.fromLngLat(
        [anchor.lon, anchor.lat],
        anchorElevM,
      );
      const scalePerMeter = anchorMerc.meterInMercatorCoordinateUnits();

      state.projectionMatrix = projectionMatrixWithExtendedClip(
        matrix as unknown as number[],
        options as CustomRenderOptions,
        anchorMerc,
        scalePerMeter,
      );

      updateInstances(state, renderState, anchor, anchorElevM, zoom, map);
      updateFlightPaths(state, renderState, zoom, map);

      state.camera.projectionMatrix = state.projectionMatrix;
      renderer.resetState();
      renderer.render(state.scene, state.camera);
      map.triggerRepaint();
    },
  };

  return {
    layer,
    pickAtPoint: (map, point) => pickInstanceAtPoint(state, map, point, opts.isHidden),
    hasHoverAtPoint: (map, point) => pickInstanceAtPoint(state, map, point, opts.isHidden) != null,
  };
}

async function loadModel(url: string, state: LayerState): Promise<LoadedModel | null> {
  const existing = state.models.get(url);
  if (existing) return existing;
  try {
    const gltf = await state.loader.loadAsync(url);
    const scene = gltf.scene;
    const box = new THREE.Box3().setFromObject(scene);
    const span = Math.max(box.max.x - box.min.x, 0.0001);
    const loaded: LoadedModel = { url, scene, span };
    state.models.set(url, loaded);
    return loaded;
  } catch {
    return null;
  }
}

function tintMaterial(obj: THREE.Object3D, colorHex: string): void {
  const color = new THREE.Color(colorHex);
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) {
      const m = mat as THREE.MeshStandardMaterial;
      if (m.isMeshStandardMaterial) {
        m.color = color;
        m.depthTest = false;
        m.depthWrite = false;
        m.side = THREE.DoubleSide;
        m.needsUpdate = true;
      }
    }
  });
}

/** MapLibre supplies only a projection matrix — disable culling that assumes a full camera. */
function disableFrustumCulling(obj: THREE.Object3D): void {
  obj.frustumCulled = false;
  obj.traverse((child) => {
    child.frustumCulled = false;
  });
}

function modelFootprintM(iconKind: string, _lat: number, zoom: number): number {
  if (usesIconMatchedFootprint(iconKind)) return helicopterModelFootprintM(zoom);
  return pillarHalfSpanM(iconKind) * 2;
}

function buildInstance(loaded: LoadedModel, iconKind: string, lat: number, zoom: number): THREE.Object3D {
  const obj = loaded.scene.clone(true);
  const wingspanM = modelFootprintM(iconKind, lat, zoom);
  const s = wingspanM / loaded.span;
  obj.scale.setScalar(s);
  obj.rotation.order = 'ZXY';
  obj.userData.modelSpan = loaded.span;
  disableFrustumCulling(obj);
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) {
      const m = mat as THREE.MeshStandardMaterial;
      if (m.isMeshStandardMaterial) {
        m.depthTest = false;
        m.depthWrite = false;
        m.side = THREE.DoubleSide;
      }
    }
  });
  return obj;
}

function ensureLabelMesh(state: LayerState, icao: string): THREE.Mesh {
  let idx = state.labelIndex.get(icao);
  if (idx == null) {
    const mesh = createLabelMesh('');
    mesh.userData.icao = icao;
    idx = state.labelMeshes.length;
    state.labelMeshes.push(mesh);
    state.labelIndex.set(icao, idx);
    state.scene.add(mesh);
  }
  return state.labelMeshes[idx];
}

function createLabelMesh(text: string): THREE.Mesh {
  const { texture, width, height } = labelTextureFor(text);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
  mesh.frustumCulled = false;
  mesh.userData.labelText = text;
  mesh.userData.labelAspect = width / height;
  mesh.renderOrder = 2;
  return mesh;
}

function labelTextureFor(text: string): { texture: THREE.CanvasTexture; width: number; height: number } {
  // Match `aircraft-label` in FlightMap.tsx: Open Sans Regular, #e6edf7 fill, #0b1020 halo 1.5.
  const fontSize = LABEL_SCREEN_PX_MAX;
  const font = `normal ${fontSize}px "Open Sans", "Open Sans Regular", "Arial Unicode MS", Arial, sans-serif`;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  ctx.font = font;
  const metrics = ctx.measureText(text);
  const width = Math.ceil(metrics.width) + 6;
  const height = Math.ceil(fontSize * 1.35);
  canvas.width = width;
  canvas.height = height;
  ctx.font = font;
  ctx.textBaseline = 'middle';
  const x = 3;
  const y = height / 2;
  ctx.lineJoin = 'round';
  ctx.lineWidth = 3; // ~1.5px halo radius, same as MapLibre text-halo-width 1.5
  ctx.strokeStyle = '#0b1020';
  ctx.strokeText(text, x, y);
  ctx.fillStyle = '#e6edf7';
  ctx.fillText(text, x, y);
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return { texture, width, height };
}

function labelTargetScreenPx(zoom: number): number {
  if (zoom <= 7) return LABEL_SCREEN_PX_MIN;
  if (zoom >= 12) return LABEL_SCREEN_PX_MAX;
  return LABEL_SCREEN_PX_MIN + (LABEL_SCREEN_PX_MAX - LABEL_SCREEN_PX_MIN) * ((zoom - 7) / 5);
}

/** World metres spanned by one screen pixel at `worldPos` (constant on-screen label size). */
function worldMetersPerScreenPixel(
  worldPos: THREE.Vector3,
  projectionMatrix: THREE.Matrix4,
  map: maplibregl.Map,
  unprojectOut: THREE.Vector3,
  scratch4: THREE.Vector4,
): number | null {
  const base = projectToScreen(worldPos, projectionMatrix, map);
  if (!base.visible) return null;
  if (!unprojectScreenAtDepth(base.x, base.y, worldPos, projectionMatrix, map, unprojectOut, scratch4)) {
    return null;
  }
  const anchor = unprojectOut.clone();
  if (!unprojectScreenAtDepth(base.x, base.y - 1, worldPos, projectionMatrix, map, unprojectOut, scratch4)) {
    return null;
  }
  const dist = anchor.distanceTo(unprojectOut);
  return dist > 1e-9 ? dist : null;
}

function labelWorldPosFromScreenOffset(
  anchorWorld: THREE.Vector3,
  screenOffsetX: number,
  screenOffsetY: number,
  projectionMatrix: THREE.Matrix4,
  map: maplibregl.Map,
  unprojectOut: THREE.Vector3,
  scratch4: THREE.Vector4,
): THREE.Vector3 | null {
  const base = projectToScreen(anchorWorld, projectionMatrix, map);
  if (!base.visible) return null;
  if (
    !unprojectScreenAtDepth(
      base.x + screenOffsetX,
      base.y + screenOffsetY,
      anchorWorld,
      projectionMatrix,
      map,
      unprojectOut,
      scratch4,
    )
  ) {
    return null;
  }
  return unprojectOut.clone();
}

function ensureLabelConnectorLine(state: LayerState, icao: string): THREE.Line {
  let idx = state.labelConnectorIndex.get(icao);
  if (idx == null) {
    const geometry = new THREE.BufferGeometry();
    const material = new THREE.LineBasicMaterial({
      color: LABEL_CONNECTOR_COLOR,
      transparent: true,
      opacity: LABEL_CONNECTOR_OPACITY,
      depthTest: false,
      depthWrite: false,
    });
    const line = new THREE.Line(geometry, material);
    line.userData.icao = icao;
    line.frustumCulled = false;
    line.renderOrder = 1;
    idx = state.labelConnectors.length;
    state.labelConnectors.push(line);
    state.labelConnectorIndex.set(icao, idx);
    state.scene.add(line);
  }
  return state.labelConnectors[idx];
}

function updateLabelScale(
  mesh: THREE.Mesh,
  worldPos: THREE.Vector3,
  projectionMatrix: THREE.Matrix4,
  map: maplibregl.Map,
  zoom: number,
  unprojectOut: THREE.Vector3,
  scratch4: THREE.Vector4,
): void {
  const metersPerPx = worldMetersPerScreenPixel(worldPos, projectionMatrix, map, unprojectOut, scratch4);
  if (metersPerPx == null) return;
  const heightM = labelTargetScreenPx(zoom) * metersPerPx;
  const aspect = (mesh.userData.labelAspect as number) ?? 1;
  mesh.scale.set(heightM * aspect, heightM, 1);
}

function setLabelText(mesh: THREE.Mesh, text: string): void {
  if (mesh.userData.labelText === text) return;
  const material = mesh.material as THREE.MeshBasicMaterial;
  material.map?.dispose();
  const { texture, width, height } = labelTextureFor(text);
  material.map = texture;
  material.needsUpdate = true;
  mesh.userData.labelText = text;
  mesh.userData.labelAspect = width / height;
}

/** Map a screen point at the same depth as `depthRef` back to ENU world metres. */
function unprojectScreenAtDepth(
  screenX: number,
  screenY: number,
  depthRef: THREE.Vector3,
  projectionMatrix: THREE.Matrix4,
  map: maplibregl.Map,
  out: THREE.Vector3,
  scratch: THREE.Vector4,
): boolean {
  const canvas = map.getCanvas();
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  const refClip = depthRef.clone().applyMatrix4(projectionMatrix);
  scratch.set((screenX / w) * 2 - 1, 1 - (screenY / h) * 2, refClip.z, 1);
  const inv = projectionMatrix.clone().invert();
  scratch.applyMatrix4(inv);
  if (Math.abs(scratch.w) < 1e-8) return false;
  out.set(scratch.x / scratch.w, scratch.y / scratch.w, scratch.z / scratch.w);
  return true;
}

function projectToScreen(
  point: THREE.Vector3,
  projectionMatrix: THREE.Matrix4,
  map: maplibregl.Map,
): { x: number; y: number; visible: boolean } {
  const clip = new THREE.Vector4(point.x, point.y, point.z, 1).applyMatrix4(projectionMatrix);
  if (clip.w <= 0) return { x: 0, y: 0, visible: false };
  const ndcX = clip.x / clip.w;
  const ndcY = clip.y / clip.w;
  if (ndcX < -1.2 || ndcX > 1.2 || ndcY < -1.2 || ndcY > 1.2) return { x: 0, y: 0, visible: false };
  const canvas = map.getCanvas();
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  return {
    x: ((ndcX + 1) / 2) * w,
    y: ((1 - ndcY) / 2) * h,
    visible: true,
  };
}

function screenRadiusPx(
  center: THREE.Vector3,
  footprintM: number,
  projectionMatrix: THREE.Matrix4,
  map: maplibregl.Map,
): number {
  const edge = center.clone().add(new THREE.Vector3(footprintM * 0.5, 0, 0));
  const c = projectToScreen(center, projectionMatrix, map);
  const e = projectToScreen(edge, projectionMatrix, map);
  if (!c.visible || !e.visible) return PICK_MIN_RADIUS_PX;
  return Math.max(PICK_MIN_RADIUS_PX, Math.hypot(e.x - c.x, e.y - c.y));
}

/** Rotate a plane so its face aligns with the viewport (MapLibre projection-only camera). */
function orientLabelToViewport(
  mesh: THREE.Mesh,
  worldPos: THREE.Vector3,
  projectionMatrix: THREE.Matrix4,
  map: maplibregl.Map,
  unprojectOut: THREE.Vector3,
  scratch4: THREE.Vector4,
): boolean {
  const base = projectToScreen(worldPos, projectionMatrix, map);
  if (!base.visible) return false;

  const right = new THREE.Vector3();
  const up = new THREE.Vector3();
  if (
    !unprojectScreenAtDepth(base.x + 10, base.y, worldPos, projectionMatrix, map, unprojectOut, scratch4)
  ) {
    return false;
  }
  right.copy(unprojectOut).sub(worldPos);
  if (
    !unprojectScreenAtDepth(base.x, base.y - 10, worldPos, projectionMatrix, map, unprojectOut, scratch4)
  ) {
    return false;
  }
  up.copy(unprojectOut).sub(worldPos);
  if (right.lengthSq() < 1e-12 || up.lengthSq() < 1e-12) return false;
  right.normalize();
  up.normalize();
  const forward = new THREE.Vector3().crossVectors(right, up).normalize();
  mesh.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(right, up, forward));
  return true;
}

function pickInstanceAtPoint(
  state: LayerState,
  map: maplibregl.Map,
  point: maplibregl.PointLike,
  isHidden: () => boolean,
): string | null {
  if (isHidden() || !state.projectionMatrix) return null;
  const px = 'x' in point ? point.x : (point as number[])[0];
  const py = 'y' in point ? point.y : (point as number[])[1];
  let best: { icao: string; dist: number } | null = null;

  for (const obj of state.instances) {
    if (!obj.visible) continue;
    const center = obj.position;
    const screen = projectToScreen(center, state.projectionMatrix, map);
    if (!screen.visible) continue;
    const radius = (obj.userData.pickRadiusPx as number | undefined) ?? PICK_MIN_RADIUS_PX;
    const dist = Math.hypot(screen.x - px, screen.y - py);
    if (dist <= radius && (!best || dist < best.dist)) {
      best = { icao: obj.userData.icao as string, dist };
    }
  }
  return best?.icao ?? null;
}

function aircraftNoseWorld(
  obj: THREE.Object3D,
  trackDeg: number,
  pitchDeg: number,
  footprintM: number,
  out: THREE.Vector3,
): THREE.Vector3 {
  // Local model offsets cannot be used here: Rx(+90°) maps fuselage-forward (+Y)
  // into world-up, so matrixWorld would place the nose ~half a wingspan too high.
  // Instead extend from the aircraft centre along true track + pitch in ENU.
  const trackRad = THREE.MathUtils.degToRad(trackDeg);
  const pitchRad = THREE.MathUtils.degToRad(pitchDeg);
  const forwardM = footprintM * NOSE_LOCAL_Y;
  const liftM = footprintM * NOSE_LOCAL_Z;
  const cosP = Math.cos(pitchRad);
  const sinP = Math.sin(pitchRad);
  return out.set(
    obj.position.x + Math.sin(trackRad) * forwardM * cosP,
    obj.position.y + Math.cos(trackRad) * forwardM * cosP,
    obj.position.z + forwardM * sinP + liftM,
  );
}

function updateInstances(
  state: LayerState,
  renderState: RenderState,
  anchor: { lon: number; lat: number },
  anchorElevM: number,
  zoom: number,
  map: maplibregl.Map,
): void {
  const { list, snapshotTimeSec, nowSec, selectedIcao, selectedFetchedAtSec } = renderState;
  const active = new Set<string>();
  const projectionMatrix = state.projectionMatrix;

  for (const ac of list) {
    if (ac.onGround) continue;
    const altFeet = ac.altBaro ?? 0;
    if (altFeet <= 0) continue;
    const dtSec = extrapolationDtFor(ac, snapshotTimeSec, nowSec, selectedIcao, selectedFetchedAtSec);
    const pos = extrapolate(ac, dtSec);
    if (!pos) continue;

    const emergency = isEmergency(ac);
    const def = getAircraftModelDef(ac.iconKind, emergency);
    const model = state.models.get(def.url);
    if (!model) continue;

    active.add(ac.icao);
    let idx = state.instanceIndex.get(ac.icao);
    if (idx == null) {
      const obj = buildInstance(model, ac.iconKind, pos.lat, zoom);
      obj.userData.icao = ac.icao;
      idx = state.instances.length;
      state.instances.push(obj);
      state.instanceIndex.set(ac.icao, idx);
      state.scene.add(obj);
    }
    const obj = state.instances[idx];

    const footprintM = modelFootprintM(ac.iconKind, pos.lat, zoom);
    const s = footprintM / model.span;
    obj.scale.setScalar(s);

    const kindKey = `${ac.iconKind}|${emergency}|${def.preserveColor}`;
    if ((obj.userData.kindKey ?? '') !== kindKey) {
      if (!def.preserveColor) tintMaterial(obj, def.color);
      obj.userData.kindKey = kindKey;
    }

    const { east, north } = lngLatToEnu(anchor.lon, anchor.lat, pos.lon, pos.lat);
    const pillarTop = aircraftSceneAltitudeM(map, pos.lon, pos.lat, altFeet, zoom, anchorElevM);
    obj.position.set(east, north, pillarTop);
    obj.renderOrder = 1;

    const pitchDeg = inferFlightPathPitch(ac.verticalRate, ac.groundSpeed);
    const yawDeg = (ac.track ?? 0) + MODEL_YAW_OFFSET_DEG;
    obj.rotation.x = THREE.MathUtils.degToRad(90 + pitchDeg);
    obj.rotation.y = 0;
    obj.rotation.z = -THREE.MathUtils.degToRad(yawDeg);

    if (projectionMatrix) {
      obj.userData.pickRadiusPx = screenRadiusPx(obj.position, footprintM, projectionMatrix, map);
    }

    obj.visible = true;
  }

  updateLabels(state, renderState, anchor, zoom, map, active);

  for (let i = 0; i < state.instances.length; i++) {
    const obj = state.instances[i];
    const icao = obj.userData.icao as string | undefined;
    if (icao && !active.has(icao)) obj.visible = false;
  }
}

function updateLabels(
  state: LayerState,
  renderState: RenderState,
  anchor: { lon: number; lat: number },
  zoom: number,
  map: maplibregl.Map,
  activeAircraft: Set<string>,
): void {
  const { list, snapshotTimeSec, nowSec, selectedIcao, selectedFetchedAtSec, preferLabelCallsign, show3DLabels } =
    renderState;
  const labelsVisible = show3DLabels && zoom >= LABEL_MIN_ZOOM;
  const projectionMatrix = state.projectionMatrix;
  const active = new Set<string>();

  if (!labelsVisible || !projectionMatrix) {
    for (const mesh of state.labelMeshes) mesh.visible = false;
    for (const line of state.labelConnectors) line.visible = false;
    return;
  }

  for (const ac of list) {
    if (!activeAircraft.has(ac.icao)) continue;
    if (ac.onGround) continue;
    const altFeet = ac.altBaro ?? 0;
    if (altFeet <= 0) continue;
    const dtSec = extrapolationDtFor(ac, snapshotTimeSec, nowSec, selectedIcao, selectedFetchedAtSec);
    const pos = extrapolate(ac, dtSec);
    if (!pos) continue;

    const objIdx = state.instanceIndex.get(ac.icao);
    if (objIdx == null) continue;
    const obj = state.instances[objIdx];
    if (!obj.visible) continue;

    const footprintM = modelFootprintM(ac.iconKind, pos.lat, zoom);
    const aircraftAnchor = obj.position.clone();
    aircraftAnchor.z += footprintM * 0.22;

    const labelPos = labelWorldPosFromScreenOffset(
      aircraftAnchor,
      LABEL_SCREEN_OFFSET_X,
      LABEL_SCREEN_OFFSET_Y,
      projectionMatrix,
      map,
      state.labelScratch,
      state.unprojectScratch,
    );
    if (!labelPos) continue;

    const mesh = ensureLabelMesh(state, ac.icao);
    setLabelText(mesh, aircraftDisplayLabel(ac, preferLabelCallsign));
    mesh.position.copy(labelPos);
    if (!orientLabelToViewport(mesh, labelPos, projectionMatrix, map, state.labelScratch, state.unprojectScratch)) {
      mesh.visible = false;
      continue;
    }
    updateLabelScale(mesh, labelPos, projectionMatrix, map, zoom, state.labelScratch, state.unprojectScratch);
    mesh.visible = true;

    const connector = ensureLabelConnectorLine(state, ac.icao);
    connector.geometry.setFromPoints([aircraftAnchor, labelPos]);
    connector.visible = true;

    active.add(ac.icao);
  }

  for (const mesh of state.labelMeshes) {
    const icao = mesh.userData.icao as string | undefined;
    if (icao && !active.has(icao)) mesh.visible = false;
  }
  for (const line of state.labelConnectors) {
    const icao = line.userData.icao as string | undefined;
    if (icao && !active.has(icao)) line.visible = false;
  }
}

function ensurePathLine(state: LayerState, icao: string): THREE.Line {
  let idx = state.pathIndex.get(icao);
  if (idx == null) {
    const geometry = new THREE.BufferGeometry();
    const material = new THREE.LineDashedMaterial({
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const line = new THREE.Line(geometry, material);
    line.userData.icao = icao;
    line.frustumCulled = false;
    line.renderOrder = 0;
    idx = state.pathLines.length;
    state.pathLines.push(line);
    state.pathIndex.set(icao, idx);
    state.scene.add(line);
  }
  return state.pathLines[idx];
}

function updateFlightPaths(
  state: LayerState,
  renderState: RenderState,
  zoom: number,
  map: maplibregl.Map,
): void {
  const { list, snapshotTimeSec, nowSec, selectedIcao, selectedFetchedAtSec, show3DPaths } = renderState;
  const active = new Set<string>();

  if (!show3DPaths) {
    for (const line of state.pathLines) line.visible = false;
    return;
  }

  for (const ac of list) {
    if (ac.onGround) continue;
    const altFeet = ac.altBaro ?? 0;
    if (altFeet <= 0) continue;
    if (ac.groundSpeed == null || ac.track == null || ac.groundSpeed < MIN_PATH_SPEED_KT) continue;

    const dtSec = extrapolationDtFor(ac, snapshotTimeSec, nowSec, selectedIcao, selectedFetchedAtSec);
    const pos = extrapolate(ac, dtSec);
    if (!pos) continue;

    const idx = state.instanceIndex.get(ac.icao);
    if (idx == null) continue;
    const obj = state.instances[idx];
    if (!obj.visible) continue;

    const footprintM = modelFootprintM(ac.iconKind, pos.lat, zoom);
    const emergency = isEmergency(ac);
    const def = getAircraftModelDef(ac.iconKind, emergency);
    const selected = ac.icao === selectedIcao;
    const seconds = selected ? FLIGHT_PATH_LOOKAHEAD_SEC * 1.5 : FLIGHT_PATH_LOOKAHEAD_SEC;
    const horizDistM = ac.groundSpeed * 0.514444 * seconds;

    const pitchDeg = inferFlightPathPitch(ac.verticalRate, ac.groundSpeed);
    const start = aircraftNoseWorld(obj, ac.track, pitchDeg, footprintM, state.noseLocal);

    const trackRad = THREE.MathUtils.degToRad(ac.track);
    const pitchRad = THREE.MathUtils.degToRad(pitchDeg);
    const groundM = horizDistM * Math.cos(pitchRad);
    const endAltFeet = altFeet + (ac.verticalRate ?? 0) * (seconds / 60);
    const end = new THREE.Vector3(
      start.x + Math.sin(trackRad) * groundM,
      start.y + Math.cos(trackRad) * groundM,
      start.z +
        horizDistM * Math.sin(pitchRad) +
        (altitudeHeightAtZoom(endAltFeet, zoom) - altitudeHeightAtZoom(altFeet, zoom)),
    );

    const line = ensurePathLine(state, ac.icao);
    line.geometry.setFromPoints([start, end]);
    line.computeLineDistances();

    const material = line.material as THREE.LineDashedMaterial;
    material.color.set(def.color);
    material.opacity = selected ? 0.7 : 0.34;
    material.dashSize = footprintM * 0.06;
    material.gapSize = footprintM * 0.045;
    material.needsUpdate = true;
    line.visible = true;
    active.add(ac.icao);
  }

  for (const line of state.pathLines) {
    const icao = line.userData.icao as string | undefined;
    if (icao && !active.has(icao)) line.visible = false;
  }
}
